import { COL_DATE_OFFSETS } from '../constants/columns';
import { AddDays, FormatBrDate, ParseIsoDate, ToIsoDate } from './dateUtils';

/**
 * Colunas cujo range NÃO é fixo (não está em COL_DATE_OFFSETS) mas segue o
 * PERÍODO ATIVO [periodStart, periodEnd]. Usado pra expor o range real no DOM.
 */
const PERIOD_RANGE_COLS = new Set([
    'periodTotal',
    'avgPeriod',
    'bestDay',
    'orderCount',
    'paidCount',
    'cancelledCount',
    'cancelPercent',
    'periodSplit',
]);

/**
 * Range ABSOLUTO (ISO UTC `YYYY-MM-DD`) que uma coluna agregada considera.
 * É a fonte da verdade pro harness E2E: ele lê este range do DOM e valida o
 * valor da célula contra a API/DB/backend NO MESMO range.
 *
 *   - COL_DATE_OFFSETS[col]: range fixo relativo ao anchor (ex sales7 = anchor-6..anchor).
 *   - PERIOD_RANGE_COLS:     range = período ativo [periodStart, periodEnd].
 *   - demais (estoque/cadastro/derivadas): null (não é uma janela de vendas).
 *
 * @param {string} col
 * @param {{anchorIso?: string, periodStartIso?: string, periodEndIso?: string}} opts
 * @returns {{from: string, to: string}|null}
 */
export function GetColumnDateRangeIso(col, { anchorIso, periodStartIso, periodEndIso } = {}) {
    const window = COL_DATE_OFFSETS[col];
    if (window) {
        const anchorDate = ParseIsoDate(anchorIso) || ParseIsoDate(ToIsoDate(new Date()));
        if (!anchorDate) return null;
        return {
            from: ToIsoDate(AddDays(anchorDate, window[0])),
            to: ToIsoDate(AddDays(anchorDate, window[1])),
        };
    }
    if (PERIOD_RANGE_COLS.has(col) && periodStartIso && periodEndIso) {
        return { from: periodStartIso, to: periodEndIso };
    }
    return null;
}

/**
 * Slice de `row.dailyAll` correspondente à janela hardcoded da coluna.
 * Usado APENAS pela DataTable em modo line/spark — colunas escalares leem
 * `row.X` direto.
 *
 *   sales3/5/7/30 → últimos N
 *   week1..4      → 7d offset N
 *   month1..3     → 30d offset N
 *
 * @param {import('../pipeline/contract').Row} row
 * @param {string} col
 * @returns {number[]}
 */
export function DailySliceForCol(row, col) {
    const all = Array.isArray(row?.dailyAll) ? row.dailyAll : null;
    if (!all || all.length === 0) return [];
    const L = all.length;
    switch (col) {
        case 'sales3': return all.slice(L - 3);
        case 'sales5': return all.slice(L - 5);
        case 'sales7': return all.slice(L - 7);
        case 'sales30': return all.slice(L - 30);
        case 'week1': return all.slice(L - 7);
        case 'week2': return all.slice(L - 14, L - 7);
        case 'week3': return all.slice(L - 21, L - 14);
        case 'week4': return all.slice(L - 28, L - 21);
        case 'month1': return all.slice(L - 30);
        case 'month2': return all.slice(L - 60, L - 30);
        case 'month3': return all.slice(L - 90, L - 60);
        default: return [];
    }
}

/**
 * Texto de range de datas representado por uma coluna (tooltip do header).
 * Devolve "DD/MM à DD/MM" ou null pra colunas sem janela.
 *
 * @param {string} col
 * @param {{anchor?: Date|string, periodDays?: number}} opts
 * @returns {string|null}
 */
export function GetColumnDateRangeText(col, { anchor, periodDays } = {}) {
    const anchorDate =
        anchor instanceof Date
            ? new Date(anchor.getTime())
            : typeof anchor === 'string'
              ? ParseIsoDate(anchor) || new Date()
              : new Date();
    anchorDate.setUTCHours(0, 0, 0, 0);

    const window = COL_DATE_OFFSETS[col];
    if (window) {
        const from = AddDays(anchorDate, window[0]);
        const to = AddDays(anchorDate, window[1]);
        if (window[0] === window[1]) return FormatBrDate(from);
        return `${FormatBrDate(from)} à ${FormatBrDate(to)}`;
    }

    if ((col === 'avgPeriod' || col === 'bestDay') && periodDays) {
        const from = AddDays(anchorDate, -(Number(periodDays) - 1));
        return `${FormatBrDate(from)} à ${FormatBrDate(anchorDate)} (${periodDays} dias)`;
    }
    return null;
}
